import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Footer from "@/components/Footer";
import BookForm from "@/pages/steps/BookForm";
import DraftView from "@/pages/steps/DraftView";
import RevisionsView from "@/pages/steps/RevisionsView";
import DeliveryView from "@/pages/steps/DeliveryView";
import { toast } from "sonner";

const stepForStatus = (s) => (s === "paid" || s === "delivered" ? 4 : s === "draft_ready" || s === "created" ? 2 : 1);
const maxForStatus = (s) => (s === "paid" || s === "delivered" ? 4 : s === "draft_ready" ? 3 : s === "created" ? 2 : 1);

export default function Studio() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [project, setProject] = useState(null);
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(Boolean(id));

  useEffect(() => {
    if (!id) { setProject(null); setStep(1); setLoading(false); return; }
    setLoading(true);
    api.get(`/projects/${id}`)
      .then((r) => { setProject(r.data); setStep(stepForStatus(r.data.status)); })
      .catch((e) => { toast.error(formatError(e.response?.data?.detail)); navigate("/studio"); })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, navigate]);

  const onCreated = (p) => { setProject(p); setStep(2); navigate(`/studio/${p.id}`, { replace: true }); };

  const maxStep = project ? maxForStatus(project.status) : 1;
  const rc = project?.revision_count || 0;
  const price = project?.price || 49;
  const paid = project && (project.status === "paid" || project.status === "delivered");

  const TABS = [
    { n: 1, t: "Datos", s: project ? "Completado" : "Empieza aquí", done: Boolean(project) },
    { n: 2, t: "Borrador", s: project && project.status !== "created" ? "Informe listo" : "Pendiente" },
    { n: 3, t: "Revisiones", s: `Revisión ${rc}` },
    { n: 4, t: "Entrega", s: paid ? "Listo" : `${price} € al final` },
  ];

  const initials = (user?.email || "AU").slice(0, 2).toUpperCase();

  return (
    <div style={{ minHeight: "100vh", background: "var(--ground)", display: "flex", flexDirection: "column" }}>
      <div style={{ flex: 1 }}>
        <section style={{ paddingBlock: "32px 64px" }}>
          <div className="wrap" style={{ maxWidth: 1040 }}>
            <div className="app">
              <div className="app-top">
                <button onClick={() => navigate("/")} data-testid="studio-brand" style={{ display: "flex", alignItems: "center", gap: 10, background: "none", border: 0, cursor: "pointer", color: "var(--ink)" }}>
                  <img src="/logo-mark.png" alt="" className="brand-mark" style={{ height: 22 }} aria-hidden="true" />
                  <span style={{ fontFamily: "var(--serif)", fontSize: 18, fontWeight: 600 }}>Jarvis <span style={{ fontStyle: "italic", fontWeight: 400 }}>Editorial</span></span>
                </button>
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <button onClick={logout} data-testid="studio-logout-btn" style={{ background: "none", border: 0, cursor: "pointer", color: "var(--muted)", fontFamily: "var(--sans)", fontSize: 14, minHeight: 44 }}>Salir</button>
                  <span className="avatar">{initials}</span>
                </div>
              </div>

              <div className="app-main" style={{ paddingBottom: 28 }}>
                <div className="book-head">
                  <div style={{ minWidth: 0 }}>
                    <h3 style={{ fontSize: 28 }}>{project ? project.title : "Nuevo libro"}</h3>
                    {project && (
                      <p style={{ color: "var(--muted)", fontSize: 14, marginTop: 4 }}>
                        {project.author_name} · <span className="chip">{project.genre}</span> <span className="chip">{project.mode_label}</span>
                      </p>
                    )}
                  </div>
                </div>

                <div className="stepper" role="tablist">
                  {TABS.map((tab) => {
                    const reachable = tab.n <= maxStep;
                    return (
                      <button key={tab.n} role="tab" type="button" data-testid={`step-${tab.n}-tab`} aria-selected={step === tab.n} disabled={!reachable} onClick={() => reachable && setStep(tab.n)}>
                        <span className={`n ${tab.n < step ? "done" : ""}`}>{tab.n < step ? "✓" : tab.n}</span>
                        <span><strong>{tab.t}</strong><small>{tab.s}</small></span>
                      </button>
                    );
                  })}
                </div>

                {loading ? (
                  <p style={{ color: "var(--muted)", fontFamily: "var(--mono)", fontSize: 13, padding: "40px 0" }}>Cargando tu proyecto…</p>
                ) : !project || step === 1 ? (
                  <BookForm onCreated={onCreated} />
                ) : step === 2 ? (
                  <DraftView project={project} onUpdate={setProject} onNext={() => setStep(3)} />
                ) : step === 3 ? (
                  <RevisionsView project={project} onUpdate={setProject} />
                ) : (
                  <DeliveryView project={project} onUpdate={setProject} />
                )}
              </div>
            </div>
          </div>
        </section>
      </div>
      <Footer />
    </div>
  );
}
